import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import {
  clearManualOptOut,
  getOptOutStatus,
  setManualOptOut,
} from "@/lib/privacyConsent";
import {
  loadShopifyConsent,
  syncOptInToShopify,
  syncOptOutToShopify,
} from "@/lib/shopifyConsent";
import { submitPrivacyOptOut } from "@/lib/privacy.functions";
import { getExternalId } from "@/lib/metaPixel";

export const Route = createFileRoute("/policies/your-privacy-choices")({
  head: () => ({
    meta: [
      { title: "Your Privacy Choices — Coyote Creek Hat Co." },
      {
        name: "description",
        content:
          "Opt out of the sale or sharing of your personal information and targeted advertising at Coyote Creek Hat Co.",
      },
      { property: "og:title", content: "Your Privacy Choices — Coyote Creek Hat Co." },
      {
        property: "og:description",
        content:
          "Opt out of the sale or sharing of your personal information and targeted advertising.",
      },
      {
        property: "og:url",
        content: "https://coyotecreekhatco.com/policies/your-privacy-choices",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://coyotecreekhatco.com/policies/your-privacy-choices",
      },
    ],
  }),
  component: YourPrivacyChoicesPage,
});

const emailSchema = z.string().trim().email().max(255);

function YourPrivacyChoicesPage() {
  const [hydrated, setHydrated] = useState(false);
  const [viaGpc, setViaGpc] = useState(false);
  const [viaManual, setViaManual] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [emailPending, setEmailPending] = useState(false);
  const [includeAccount, setIncludeAccount] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const s = getOptOutStatus();
    setViaGpc(s.viaGpc);
    setViaManual(s.viaManual);
    setConfirmed(s.viaManual);
    setHydrated(true);
    // Prime the Shopify Customer Privacy API for potential sync.
    loadShopifyConsent().catch(() => {});
  }, []);

  async function handleOptOut() {
    setError(null);

    if (includeAccount) {
      const parsed = emailSchema.safeParse(email);
      if (!parsed.success) {
        setError("Please enter a valid email address.");
        return;
      }
    }

    setSubmitting(true);
    setManualOptOut();

    if (includeAccount) {
      const normalized = email.trim().toLowerCase();
      try {
        await supabase
          .from("privacy_optouts")
          .insert({ email: normalized, source: "storefront" });
      } catch {
        // non-blocking: device opt-out already applied
      }

      // Request a confirmation email. Nothing changes on the account until
      // the recipient clicks the one-time link, which proves they own it.
      try {
        void submitPrivacyOptOut({
          data: {
            email: normalized,
            optOut: true,
            externalId: getExternalId(),
          },
        });
      } catch {
        // ignore
      }
      setEmailPending(true);
    }

    try {
      await syncOptOutToShopify();
    } catch {
      // non-blocking: local device opt-out already applied
    }

    setViaManual(true);
    setConfirmed(true);
    setSubmitting(false);
  }

  function handleOptBackIn() {
    clearManualOptOut();
    syncOptInToShopify().catch(() => {});
    if (email) {
      const normalized = email.trim().toLowerCase();
      try {
        void submitPrivacyOptOut({
          data: { email: normalized, optOut: false, externalId: getExternalId() },
        });
      } catch {
        // ignore
      }
    }
    setViaManual(false);
    setConfirmed(false);
    setEmailPending(false);
    setIncludeAccount(false);
    setEmail("");
    setError(null);
  }


  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="bg-[#FDFAF3]">
        <div className="mx-auto max-w-[1400px] px-6 pt-8 pb-16 text-center">
          <h1 className="font-heading text-5xl uppercase tracking-wide text-primary md:text-6xl">
            Your Privacy Choices
          </h1>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-3xl px-6 py-16">
        <div className="space-y-5 font-body text-base leading-relaxed text-foreground">
          <p>
            As described in our Privacy Policy, we collect personal information from your
            interactions with us and our website, including through cookies and similar
            technologies. We may also share this personal information with third parties,
            including advertising partners. We do this in order to show you ads on other
            websites that are more relevant to your interests and for other reasons
            outlined in our privacy policy.
          </p>
          <p>
            Sharing of personal information for targeted advertising based on your
            interaction on different websites may be considered "sales", "sharing", or
            "targeted advertising" under certain U.S. state privacy laws. Depending on
            where you live, you may have the right to opt out of these activities. If you
            would like to exercise this opt-out right, please follow the instructions
            below.
          </p>
          <p>
            If you visit our website with the Global Privacy Control opt-out preference
            signal enabled, depending on where you are, we will treat this as a request to
            opt-out of activity that may be considered a "sale" or "sharing" of personal
            information or other uses that may be considered targeted advertising for the
            device and browser you used to visit our website.
          </p>
          <p>
            By clicking "Opt out", the browser on this device will be opted out of sharing
            personal data immediately. If you select the checkbox and enter an email, we
            will send a confirmation link to that address — the related customer account is
            opted out only after you click that link, so no one can change your preferences
            on your behalf.
          </p>
        </div>

        {hydrated && viaGpc && (
          <div className="mt-8 rounded-md border-2 border-[#2A1510] bg-[#FDFAF3] p-4 font-body text-sm text-foreground">
            Your browser is sending a Global Privacy Control signal, so you're already
            opted out on this device.
          </div>
        )}

        {/* Controls / Confirmation */}
        <div className="mt-10 border-t border-[#D4CCBC] pt-10">
          {confirmed ? (
            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 grid h-7 w-7 place-items-center rounded-full bg-green-600 text-white">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </span>
                <p className="font-heading text-xl uppercase tracking-wide text-primary">
                  This device has been opted out.
                </p>
              </div>
              {emailPending && (
                <p className="font-body text-base leading-relaxed text-foreground">
                  Check your email — we sent a link to confirm the opt-out for that
                  address. The link is good for 24 hours. Your account preferences change
                  once you click it.
                </p>
              )}
              <button
                onClick={handleOptBackIn}
                className="font-ui text-xs font-bold uppercase tracking-[0.2em] text-[#8B6F47] underline underline-offset-4 hover:text-blaze"
              >
                Allow data sharing again
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeAccount}
                  onChange={(e) => setIncludeAccount(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#FA5021]"
                />
                <span className="font-body text-sm text-foreground">
                  Don't share data from my account (optional)
                </span>
              </label>

              {includeAccount && (
                <div className="space-y-2">
                  <label
                    htmlFor="privacy-email"
                    className="block font-ui text-xs font-bold uppercase tracking-[0.2em] text-primary"
                  >
                    Email
                  </label>
                  <input
                    id="privacy-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={255}
                    placeholder="your@email.com"
                    className="w-full max-w-md rounded-md border-2 border-[#2A1510] bg-white px-4 py-3 font-body text-sm text-foreground placeholder:text-[#8B6F47]/60 focus:border-blaze focus:outline-none"
                  />
                </div>
              )}

              {error && (
                <p className="font-body text-sm text-red-700">{error}</p>
              )}

              <button
                onClick={handleOptOut}
                disabled={submitting}
                className="btn-cc-primary tracking-[0.2em] disabled:opacity-60"
              >
                {submitting ? "Opting out…" : "Opt out"}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
